/**
 * PolicyEditorModal Component (DC-1096)
 *
 * Modal for adopting a policy template with customizations.
 * Allows filling in template variables and previewing the generated policy.
 *
 * Features:
 * - Template selector dropdown (if not pre-selected)
 * - Display policy markdown with customization placeholders highlighted
 * - Form fields for each required_variable from template
 * - Live preview panel showing rendered policy
 * - Copy-to-clipboard for generated policy
 *
 * @version 1.0.0
 * @date 2026-01-26
 */

import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { createPortal } from 'react-dom';
import {
  usePolicyTemplates,
  usePolicyTemplate,
  adoptPolicy,
} from './useGovernanceActions';
import type {
  PolicyEditorModalProps,
  PolicyVariable,
} from './types';
import './PolicyEditorModal.css';

export const PolicyEditorModal: React.FC<PolicyEditorModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
  journeyId,
  recommendationId,
  recommendationTitle,
  policyTemplateId,
  allRecommendations,
}) => {
  const { templates, loading: loadingTemplates } = usePolicyTemplates();

  const [selectedTemplateId, setSelectedTemplateId] = useState<string>(policyTemplateId || '');
  const { template, loading: loadingTemplate } = usePolicyTemplate(selectedTemplateId);

  const [variables, setVariables] = useState<Record<string, string>>({});
  const [showPreview, setShowPreview] = useState(false);
  const [showCoveredRecs, setShowCoveredRecs] = useState(false);
  const [copied, setCopied] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [validationErrors, setValidationErrors] = useState<string[]>([]);
  const [success, setSuccess] = useState(false);

  // Initialize variables when template loads, with auto-fill for common fields
  useEffect(() => {
    if (template && template.required_variables) {
      // Try to read org name from localStorage user context
      let orgName = '';
      try {
        const userJson = localStorage.getItem('compass_user');
        if (userJson) {
          const user = JSON.parse(userJson);
          orgName = user.organization?.name || '';
        }
      } catch { /* ignore */ }

      const today = new Date().toISOString().split('T')[0]; // YYYY-MM-DD

      const initialVars: Record<string, string> = {};
      for (const v of template.required_variables) {
        // Use default value first, then try auto-fill for common variable names
        let value = v.default_value || '';
        if (!value) {
          const lowerName = v.name.toLowerCase();
          if (lowerName.includes('organization_name') || lowerName.includes('org_name')) {
            value = orgName;
          } else if (lowerName.includes('effective_date') || lowerName === 'date') {
            value = today;
          } else if (lowerName.includes('review_date') || lowerName.includes('next_review')) {
            // Default review date: 1 year from now
            const nextYear = new Date();
            nextYear.setFullYear(nextYear.getFullYear() + 1);
            value = nextYear.toISOString().split('T')[0];
          }
        }
        initialVars[v.name] = value;
      }
      setVariables(initialVars);
    }
  }, [template]);

  // Pre-select template if provided
  useEffect(() => {
    if (policyTemplateId && !selectedTemplateId) {
      setSelectedTemplateId(policyTemplateId);
    }
  }, [policyTemplateId, selectedTemplateId]);

  // Handle ESC key
  useEffect(() => {
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen && !submitting) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEsc);
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.removeEventListener('keydown', handleEsc);
      document.body.style.overflow = 'unset';
    };
  }, [isOpen, submitting, onClose]);

  const handleBackdropClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === e.currentTarget && !submitting) {
      onClose();
    }
  };

  // Render the policy with variables filled in
  const renderedPolicy = useMemo(() => {
    if (!template) return '';

    let content = template.template_content;
    for (const [key, value] of Object.entries(variables)) {
      const placeholder = `{{${key}}}`;
      if (value) {
        content = content.split(placeholder).join(value);
      }
    }
    return content;
  }, [template, variables]);

  // Get unfilled required variables
  const unfilledVars = useMemo(() => {
    if (!template) return [];

    return template.required_variables
      .filter(v => v.required !== false && !variables[v.name])
      .map(v => v.name);
  }, [template, variables]);

  const handleVariableChange = (name: string, value: string) => {
    setVariables(prev => ({ ...prev, [name]: value }));
    setValidationErrors([]);
  };

  const handleCopyToClipboard = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(renderedPolicy);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('[PolicyEditorModal] Copy failed:', err);
    }
  }, [renderedPolicy]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!selectedTemplateId) {
      setError('Please select a policy template');
      return;
    }

    // Validate required variables
    if (unfilledVars.length > 0) {
      setValidationErrors(unfilledVars.map(v => `${formatVariableName(v)} is required`));
      return;
    }

    try {
      setSubmitting(true);
      setError(null);
      setValidationErrors([]);

      const action = await adoptPolicy({
        journey_id: journeyId,
        recommendation_id: recommendationId,
        recommendation_title: recommendationTitle,
        policy_template_id: selectedTemplateId,
        template_name: template?.name,
        customizations: variables,
      });

      setSuccess(true);
      setTimeout(() => {
        onSuccess(action);
        onClose();
      }, 1500);
    } catch (err: unknown) {
      console.error('[PolicyEditorModal] Submit error:', err);
      setError(err instanceof Error ? err.message : 'Failed to adopt policy');
    } finally {
      setSubmitting(false);
    }
  };

  const formatVariableName = (name: string): string => {
    return name
      .split('_')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };

  const renderVariableInput = (variable: PolicyVariable) => {
    const { name, type, options, description } = variable;
    const value = variables[name] || '';

    switch (type) {
      case 'select':
        return (
          <select
            id={name}
            className="form-select"
            value={value}
            onChange={(e) => handleVariableChange(name, e.target.value)}
            disabled={submitting}
          >
            <option value="">-- Select --</option>
            {options?.map((opt) => (
              <option key={opt} value={opt}>{opt}</option>
            ))}
          </select>
        );

      case 'multiselect':
        return (
          <select
            id={name}
            className="form-select"
            multiple
            value={value.split(',').filter(Boolean)}
            onChange={(e) => {
              const selected = Array.from(e.target.selectedOptions).map(o => o.value);
              handleVariableChange(name, selected.join(', '));
            }}
            disabled={submitting}
          >
            {options?.map((opt) => (
              <option key={opt} value={opt}>{opt}</option>
            ))}
          </select>
        );

      case 'date':
        return (
          <input
            id={name}
            type="date"
            className="form-input"
            value={value}
            onChange={(e) => handleVariableChange(name, e.target.value)}
            disabled={submitting}
          />
        );

      case 'number':
        return (
          <input
            id={name}
            type="number"
            className="form-input"
            value={value}
            onChange={(e) => handleVariableChange(name, e.target.value)}
            disabled={submitting}
            placeholder={description}
          />
        );

      default:
        return (
          <input
            id={name}
            type="text"
            className="form-input"
            value={value}
            onChange={(e) => handleVariableChange(name, e.target.value)}
            disabled={submitting}
            placeholder={description}
          />
        );
    }
  };

  if (!isOpen) return null;

  const loading = loadingTemplates || loadingTemplate;

  return createPortal(
    <div
      className="modal-overlay policy-modal-overlay"
      onClick={handleBackdropClick}
      role="dialog"
      aria-modal="true"
      aria-labelledby="policy-editor-title"
    >
      <div className="modal-container policy-editor-modal">
        <div className="modal-header">
          <h2 id="policy-editor-title" className="modal-title">
            {showPreview ? 'Preview Policy' : 'Configure Policy'}
          </h2>
          <button
            type="button"
            className="modal-close-btn"
            onClick={onClose}
            disabled={submitting}
            aria-label="Close modal"
          >
            &times;
          </button>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="modal-body">
            {/* Recommendation Context */}
            {recommendationTitle && (
              <div className="recommendation-context">
                <span className="context-label">From recommendation:</span>
                <span className="context-value">{recommendationTitle}</span>
              </div>
            )}

            {!showPreview ? (
              <>
                {/* Template Selector */}
                {!policyTemplateId && (
                  <div className="form-group">
                    <label htmlFor="template-select" className="form-label">
                      Select Policy Template
                    </label>
                    <select
                      id="template-select"
                      className="form-select"
                      value={selectedTemplateId}
                      onChange={(e) => setSelectedTemplateId(e.target.value)}
                      disabled={loading || submitting}
                    >
                      <option value="">-- Select a template --</option>
                      {templates.map((t) => (
                        <option key={t.template_id} value={t.template_id}>
                          {t.name} ({t.category})
                        </option>
                      ))}
                    </select>
                  </div>
                )}

                {/* Template Info */}
                {template && (
                  <div className="template-info">
                    <div className="template-header">
                      <h3 className="template-name">{template.name}</h3>
                      <span className="template-category">{template.category}</span>
                    </div>
                    {template.description && (
                      <p className="template-description">{template.description}</p>
                    )}
                    {template.compliance_frameworks.length > 0 && (
                      <div className="compliance-frameworks">
                        <span className="frameworks-label">Compliance:</span>
                        {template.compliance_frameworks.map((f, idx) => (
                          <span key={idx} className="framework-badge">{f}</span>
                        ))}
                      </div>
                    )}
                  </div>
                )}

                {/* Covered Recommendations (collapsible) */}
                {template && allRecommendations && allRecommendations.length > 0 && (
                  <div className="covered-recommendations">
                    <button
                      type="button"
                      className="toggle-covered-btn"
                      onClick={() => setShowCoveredRecs(!showCoveredRecs)}
                    >
                      {showCoveredRecs ? '▾' : '▸'} Recommendations covered ({allRecommendations.length})
                    </button>
                    {showCoveredRecs && (
                      <ul className="covered-list">
                        {allRecommendations.map(rec => (
                          <li key={rec.id} className="covered-item">
                            <span className={`covered-priority priority-${rec.priority}`}>
                              {rec.priority === 'critical' ? 'C' : rec.priority === 'warning' ? 'W' : 'O'}
                            </span>
                            {rec.title}
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                )}

                {/* Variable Inputs */}
                {template && template.required_variables.length > 0 && (
                  <div className="variables-section">
                    <h4 className="section-title">Customize Policy</h4>
                    <div className="variables-grid">
                      {template.required_variables.map((variable) => (
                        <div key={variable.name} className="form-group variable-input">
                          <label htmlFor={variable.name} className="form-label">
                            {formatVariableName(variable.name)}
                            {variable.required !== false && <span className="required-marker">*</span>}
                          </label>
                          {variable.description && (
                            <span className="input-hint">{variable.description}</span>
                          )}
                          {renderVariableInput(variable)}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* No Template Selected */}
                {!template && selectedTemplateId === '' && !loadingTemplates && (
                  <div className="empty-state">
                    {templates.length === 0
                      ? 'No policy templates found. Run the governance template seeder or contact your administrator.'
                      : 'Select a policy template to begin'}
                  </div>
                )}

                {/* Loading State */}
                {loadingTemplate && selectedTemplateId && (
                  <div className="loading-state">Loading template...</div>
                )}
              </>
            ) : (
              /* Preview Panel */
              <div className="preview-section">
                <div className="preview-toolbar">
                  <button
                    type="button"
                    className="btn btn-secondary btn-sm"
                    onClick={() => setShowPreview(false)}
                  >
                    Back to Edit
                  </button>
                  <button
                    type="button"
                    className="btn btn-secondary btn-sm"
                    onClick={handleCopyToClipboard}
                  >
                    {copied ? 'Copied!' : 'Copy to Clipboard'}
                  </button>
                </div>

                {unfilledVars.length > 0 && (
                  <div className="unfilled-warning">
                    <span className="warning-icon">⚠️</span>
                    Some required fields are not filled: {unfilledVars.map(formatVariableName).join(', ')}
                  </div>
                )}

                <div className="preview-content">
                  <pre className="policy-preview">{renderedPolicy}</pre>
                </div>
              </div>
            )}

            {/* Validation Errors */}
            {validationErrors.length > 0 && (
              <div className="validation-errors" role="alert">
                <ul>
                  {validationErrors.map((err, idx) => (
                    <li key={idx}>{err}</li>
                  ))}
                </ul>
              </div>
            )}

            {/* Error Message */}
            {error && (
              <div className="error-message" role="alert">
                {error}
              </div>
            )}

            {/* Success Message */}
            {success && (
              <div className="success-message" role="status">
                Policy adopted successfully!
              </div>
            )}
          </div>

          <div className="modal-footer">
            <button
              type="button"
              className="btn btn-secondary"
              onClick={onClose}
              disabled={submitting}
            >
              Cancel
            </button>
            {template && !showPreview && (
              <button
                type="button"
                className="btn btn-secondary"
                onClick={() => setShowPreview(true)}
                disabled={!selectedTemplateId}
              >
                Preview
              </button>
            )}
            <button
              type="submit"
              className="btn btn-primary"
              disabled={!selectedTemplateId || submitting || loading || success}
            >
              {submitting ? 'Adopting...' : 'Adopt Policy'}
            </button>
          </div>
        </form>
      </div>
    </div>,
    document.body
  );
};

export default PolicyEditorModal;
